import { useMemo } from 'react'
import {
  type APIError,
  type ConnectLogEvent,
  type ConnectLogsInput,
  type ConnectLogsOutput,
  type ConnectLogsSuccess,
  type ConnectMetricsInput,
  type ConnectMetricsOutput,
  type ConnectMetricsSuccess,
  isDefined,
  isEmpty,
  messageFromError,
} from '@terros/common'
import { type InfiniteData, type QueryKey, useInfiniteQuery, type UseInfiniteQueryResult } from '@tanstack/react-query'
import { useTantalimQuery } from '../query'
import { type GetHookResponse } from '../hooks'
import { useServerRequest } from '../api'
import { connectLogsInfiniteKey, connectMetricsKey } from './keys'

export type ConnectLogsPageInput = Omit<ConnectLogsInput, 'nextToken'>

type UseConnectLogsInfiniteResponse = Omit<UseInfiniteQueryResult<InfiniteData<ConnectLogsOutput>>, 'error'> & {
  error?: string
  logs: ConnectLogEvent[]
}

export function useConnectMetrics(input: ConnectMetricsInput | undefined): GetHookResponse<ConnectMetricsOutput> {
  return useTantalimQuery<ConnectMetricsInput | undefined, ConnectMetricsSuccess, ConnectMetricsOutput>({
    url: '/connect/app/metrics',
    queryKey: connectMetricsKey(input),
    input,
    convertResponse: (response) => response,
    options: { enabled: isDefined(input) },
  })
}

export function useConnectLogsInfinite(input: ConnectLogsPageInput | undefined): UseConnectLogsInfiniteResponse {
  const serverRequest = useServerRequest<ConnectLogsInput | undefined, ConnectLogsSuccess>('/connect/app/logs')

  const query = useInfiniteQuery<
    ConnectLogsOutput,
    APIError,
    InfiniteData<ConnectLogsOutput>,
    QueryKey,
    ConnectLogsInput | undefined
  >({
    queryKey: connectLogsInfiniteKey(input),
    queryFn: async ({ pageParam }) => serverRequest(pageParam),
    initialPageParam: input,
    getNextPageParam: (lastPage) => {
      if (!input) return undefined
      if (lastPage.type !== 'success') return undefined
      if (isEmpty(lastPage.logs)) return undefined
      if (!lastPage.nextToken) return undefined
      return { ...input, nextToken: lastPage.nextToken }
    },
    enabled: isDefined(input),
  })

  const error = messageFromError(query.error)
  const logs = useMemo(() => {
    return query.data?.pages.flatMap((page) => (page.type === 'success' ? page.logs : [])) ?? []
  }, [query.data])

  return { ...query, error, logs }
}
